<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        v-bind:title="'Import Phiếu yêu cầu bổ sung ngân sách'"
      >
        <template v-slot:preview>
          <!-- ===== Bước 1 + 2: template & upload ===== -->
          <b-overlay :show="isProcessing" rounded opacity="0.6" spinner-small>
            <b-row class="mb-3">
              <b-col>
                <div class="form-group mb-2">
                  <span>
                    <b>Bước 1:</b> Vui lòng tải file Excel mẫu đúng format
                    <a :href="urlTemplate">tại đây</a>
                  </span>
                </div>
                <span class="d-block mb-2">
                  <b>Bước 2:</b> Tải file Excel để hệ thống đọc &amp; xem trước
                </span>
                <UploadFileMulter
                  :cmdUrl="'v2/budget-line-request-imports/transform'"
                  v-on:send-file="onTransformedExcel"
                  v-on:clear-file="onClearExcel"
                />
                <span v-if="fileName" class="d-block mt-2">
                  Tên file: <b>{{ fileName }}</b>
                </span>
              </b-col>
            </b-row>
          </b-overlay>

          <!-- ===== Preview ===== -->
          <template v-if="rows.length">
            <b-row class="mb-2">
              <b-col>
                <div class="d-flex align-items-center flex-wrap">
                  <h5 class="mb-0 mr-3">Xem trước dữ liệu ({{ rows.length }} dòng)</h5>
                  <b-badge variant="success">{{ validRows.length }} hợp lệ</b-badge>
                  <b-badge v-if="errorRows.length" variant="danger" class="ml-1">
                    {{ errorRows.length }} lỗi
                  </b-badge>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col class="d-flex align-items-center justify-content-between flex-wrap">
                <b-form-checkbox
                  v-model="showOnlyErrors"
                  :disabled="isProcessing || !errorRows.length"
                  switch
                >
                  Chỉ hiện dòng lỗi
                </b-form-checkbox>
                <b-button
                  v-if="errorRows.length"
                  size="sm"
                  variant="outline-danger"
                  :disabled="isProcessing"
                  @click="removeAllErrorRows"
                >
                  <i class="fas fa-trash"></i>
                  Xoá tất cả {{ errorRows.length }} dòng lỗi
                </b-button>
              </b-col>
            </b-row>

            <b-alert v-if="errorRows.length" show variant="warning" class="mb-3">
              Còn {{ errorRows.length }} dòng lỗi — phải xoá hết mới xác nhận Import được.
            </b-alert>

            <b-row class="mb-3">
              <b-col>
                <b-table
                  :items="pagedRows"
                  :fields="previewFields"
                  :tbody-tr-class="rowClass"
                  bordered
                  hover
                  small
                  responsive
                  class="preview-table"
                >
                  <template #cell(period)="row">
                    T{{ row.item.month }}/{{ row.item.year }}
                  </template>
                  <template #cell(allocatedAmount)="row">
                    {{ money(row.item.currentBudget && row.item.currentBudget.allocatedAmount) }}
                  </template>
                  <template #cell(usedAmount)="row">
                    {{ money(row.item.currentBudget && row.item.currentBudget.usedAmount) }}
                  </template>
                  <template #cell(remainingAmount)="row">
                    {{ money(row.item.currentBudget && row.item.currentBudget.remainingAmount) }}
                  </template>
                  <template #cell(amount)="row">
                    <span class="font-weight-bold">{{ money(row.item.amount) }}</span>
                  </template>
                  <template #cell(newAllocatedAmount)="row">
                    {{ money(row.item.projected && row.item.projected.newAllocatedAmount) }}
                  </template>
                  <template #cell(newRemainingAmount)="row">
                    {{ money(row.item.projected && row.item.projected.newRemainingAmount) }}
                  </template>
                  <template #cell(errorMessage)="row">
                    <span v-if="row.item.errorMessage" class="text-danger">
                      <i class="fas fa-exclamation-triangle mr-1"></i>
                      {{ row.item.errorMessage }}
                    </span>
                    <span v-else class="text-success">
                      <i class="fas fa-check"></i>
                    </span>
                  </template>
                  <template #cell(actions)="row">
                    <span
                      class="flaticon2-rubbish-bin-delete-button text-danger"
                      style="cursor: pointer; font-size: 1rem"
                      title="Xoá dòng này"
                      @click="removeRow(row.item)"
                    />
                  </template>
                </b-table>

                <div
                  v-if="filteredRows.length > perPage"
                  class="d-flex align-items-center justify-content-between mt-2 custom-pagination"
                >
                  <div>
                    Hiển thị {{ startRow }}–{{ endRow }} / {{ filteredRows.length }} dòng
                  </div>
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredRows.length"
                    :per-page="perPage"
                    size="sm"
                    align="right"
                    class="mb-0"
                  />
                </div>

                <div class="text-right mt-2">
                  Tổng bổ sung:
                  <strong class="text-primary">{{ money(totalAmount) }} đ</strong>
                </div>
              </b-col>
            </b-row>

            <!-- ===== Quy trình duyệt ===== -->
            <b-row class="mb-4">
              <b-col cols="12" md="8">
                <b-form-group>
                  <template #label>
                    Quy trình duyệt <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    v-model="selectedApprovalConfigId"
                    :options="approvalProcessOptions"
                    :disabled="isProcessing || !approvalProcesses.length"
                    size="sm"
                    @change="onSelectApprovalProcess"
                  />
                  <small
                    v-if="!approvalProcesses.length && approvalProcessesFailed"
                    class="text-danger d-block mt-1"
                  >
                    Không tải được danh sách quy trình duyệt.
                    <a href="#" @click.prevent="fetchApprovalProcesses">Thử lại</a>
                  </small>
                  <small
                    v-else-if="!approvalProcesses.length"
                    class="text-danger d-block mt-1"
                  >
                    Chưa có quy trình duyệt 3 bước nào đang hoạt động cho phiếu bổ sung ngân sách.
                  </small>
                </b-form-group>

                <ApprovalStepSearchSelector
                  v-if="approvalSteps.length"
                  :steps="approvalSteps"
                  :title="'Người duyệt từng bước'"
                  :emptyText="'Quy trình chưa cấu hình bước duyệt nào.'"
                  @change="onApprovalStepsChange"
                />
              </b-col>
            </b-row>

            <!-- ===== Actions ===== -->
            <b-row>
              <b-col>
                <SaveButton
                  :is-busy="isSubmitting('commit')"
                  :disabled="!canCommit"
                  :button-style="{ fontWeight: '600', minWidth: '230px' }"
                  type="button"
                  @click="onConfirmImport"
                >
                  <i class="fas fa-check mr-1"></i>
                  Xác nhận Import và Gửi duyệt
                </SaveButton>
                <b-button
                  variant="secondary"
                  class="ml-2"
                  :disabled="isProcessing"
                  @click="goBack"
                >
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </template>

          <b-row v-else>
            <b-col>
              <b-button variant="secondary" :disabled="isProcessing" @click="goBack">
                <i class="fas fa-arrow-left"></i> Quay lại
              </b-button>
            </b-col>
          </b-row>

          <!-- ===== Modal xác nhận ===== -->
          <b-modal
            ref="confirmModal"
            title="Xác nhận Import"
            ok-title="Xác nhận"
            cancel-title="Huỷ"
            ok-variant="primary"
            @ok="doCommitImport"
          >
            <p class="mb-2">
              Hệ thống sẽ tạo <strong>{{ groups.length }}</strong> phiếu yêu cầu bổ sung
              ngân sách từ <strong>{{ validRows.length }}</strong> dòng dữ liệu, tổng số
              tiền <strong>{{ money(totalAmount) }} đ</strong>, và
              <strong>gửi duyệt ngay</strong> theo quy trình
              <strong>{{ selectedApprovalProcessName }}</strong>.
            </p>
            <p class="mb-0 text-muted">
              Nếu có bất kỳ lỗi nào trong quá trình tạo, toàn bộ sẽ được huỷ bỏ và không
              phiếu nào được tạo.
            </p>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice, handleError } from '@/utils/common';
import UploadFileMulter from '@/view/modules/upload/UploadFileMulter.vue';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLock from '@/view/mixins/submit-lock.mixins';
import { BASE_URL } from '@/utils/constants';

const REASON_MAX_LENGTH = 1000;

export default {
  components: {
    KTCodePreview,
    UploadFileMulter,
    ApprovalStepSearchSelector,
    SaveButton,
  },
  mixins: [submitLock],
  data() {
    return {
      urlTemplate: BASE_URL + 'templates/download?type=budget-line-request-import',
      fileName: '',
      rows: [],
      showOnlyErrors: false,
      perPage: 20,
      currentPage: 1,
      approvalProcesses: [],
      approvalProcessesFailed: false,
      selectedApprovalConfigId: null,
      approvalSteps: [],
      previewFields: [
        { key: 'rowIndex', label: '#', thStyle: { width: '48px' }, class: 'text-center' },
        { key: 'companyName', label: 'Doanh nghiệp' },
        { key: 'departmentName', label: 'Phòng ban' },
        { key: 'storeName', label: 'Địa điểm' },
        { key: 'costItemName', label: 'Khoản mục' },
        { key: 'costGroupName', label: 'Nhóm chi phí' },
        { key: 'period', label: 'Kỳ', class: 'text-center' },
        { key: 'allocatedAmount', label: 'NS ban đầu', tdClass: 'text-right' },
        { key: 'usedAmount', label: 'Đã sử dụng', tdClass: 'text-right' },
        { key: 'remainingAmount', label: 'Còn lại', tdClass: 'text-right' },
        { key: 'amount', label: 'Bổ sung', tdClass: 'text-right' },
        { key: 'newAllocatedAmount', label: 'NS mới', tdClass: 'text-right' },
        { key: 'newRemainingAmount', label: 'Còn lại mới', tdClass: 'text-right' },
        { key: 'errorMessage', label: 'Trạng thái' },
        { key: 'actions', label: '', thStyle: { width: '48px' }, class: 'text-center' },
      ],
    };
  },
  computed: {
    /** Khoá cả nhóm control khi đang commit (Rule 11 mục 3b). Không phủ bước upload. */
    isProcessing() {
      return this.isSubmitting('commit');
    },
    validRows() {
      return this.rows.filter((r) => r.isValid);
    },
    errorRows() {
      return this.rows.filter((r) => !r.isValid);
    },
    filteredRows() {
      return this.showOnlyErrors ? this.errorRows : this.rows;
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
    startRow() {
      if (!this.filteredRows.length) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredRows.length);
    },
    totalAmount() {
      return this.validRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    },
    /**
     * Gom nhóm ngay trên FE thay vì dùng `groups` của BE, vì người dùng được xoá
     * dòng ở màn preview — nhóm phải tính lại theo các dòng CÒN LẠI, và chính
     * mảng này là payload gửi lên `commit`.
     */
    groups() {
      const map = new Map();
      this.validRows.forEach((row) => {
        const key = [
          row.companyId,
          row.departmentId,
          row.storeId || 0,
          row.costItemId,
        ].join('_');
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      });

      return [...map.values()].map((groupRows) => {
        const sorted = [...groupRows].sort((a, b) => a.month - b.month);
        const sample = sorted[0];
        return {
          companyId: sample.companyId,
          companyName: sample.companyName,
          departmentId: sample.departmentId,
          departmentName: sample.departmentName,
          storeId: sample.storeId || null,
          storeName: sample.storeName || '',
          costItemId: sample.costItemId,
          costItemName: sample.costItemName,
          reason: this.mergeReasons(sorted),
          periods: sorted.map((r) => ({
            month: Number(r.month),
            year: Number(r.year),
            amount: Number(r.amount),
          })),
        };
      });
    },
    approvalProcessOptions() {
      return [
        { value: null, text: '-- Chọn quy trình duyệt --' },
        ...this.approvalProcesses.map((p) => ({
          value: p.approvalConfigId,
          text: `${p.name} (${p.numOfStep} bước)`,
        })),
      ];
    },
    selectedApprovalProcessName() {
      const found = this.approvalProcesses.find(
        (p) => Number(p.approvalConfigId) === Number(this.selectedApprovalConfigId),
      );
      return found ? found.name : '';
    },
    canCommit() {
      if (this.isProcessing) return false;
      if (!this.validRows.length) return false;
      if (this.errorRows.length) return false;
      if (!this.selectedApprovalConfigId) return false;
      if (!this.approvalSteps.length) return false;
      return this.approvalSteps.every((step) => !!step.approverBy);
    },
  },
  watch: {
    filteredRows() {
      const maxPage = Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
      if (this.currentPage > maxPage) this.currentPage = maxPage;
    },
    /**
     * Hết dòng lỗi thì tắt switch "Chỉ hiện dòng lỗi" — nếu không, switch vừa bị
     * `:disabled` vừa còn checked → bảng rỗng và không gạt lại được.
     */
    errorRows(rows) {
      if (!rows.length && this.showOnlyErrors) {
        this.showOnlyErrors = false;
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
      { title: 'Import Excel' },
    ]);
    this.fetchApprovalProcesses();
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    /** Định dạng tiền; `null`/`undefined` ra `—` chứ không ra `0` (`Number(null) === 0`). */
    money(value) {
      if (value === null || value === undefined || value === '') return '—';
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '—';
      return convertPrice(numeric);
    },
    rowClass(item) {
      if (!item) return '';
      return item.isValid ? '' : 'table-danger';
    },
    /** Giữ đồng bộ với `mergeReasons` phía BE (spec A8). */
    mergeReasons(rows) {
      const reasons = rows.map((r) => String(r.reason || '').trim()).filter(Boolean);
      if (!reasons.length) return '';
      const unique = [...new Set(reasons)];
      const merged =
        unique.length === 1
          ? unique[0]
          : rows
              .filter((r) => String(r.reason || '').trim())
              .map((r) => `T${r.month}: ${String(r.reason).trim()}`)
              .join(' | ');
      return merged.length > REASON_MAX_LENGTH
        ? merged.slice(0, REASON_MAX_LENGTH)
        : merged;
    },

    onTransformedExcel(payload, uploadedName) {
      const data = payload || {};
      this.rows = Array.isArray(data.rows) ? data.rows : [];
      this.fileName = data.fileName || uploadedName || '';
      this.currentPage = 1;
      this.showOnlyErrors = false;
      if (!this.rows.length) {
        this.makeToastFaile('File không có dòng dữ liệu nào.');
      }
    },
    onClearExcel() {
      this.rows = [];
      this.fileName = '';
      this.currentPage = 1;
      this.showOnlyErrors = false;
      // Dọn cả quy trình duyệt, không thì file sau mang theo lựa chọn cũ đã bị ẩn đi.
      this.selectedApprovalConfigId = null;
      this.approvalSteps = [];
    },
    removeRow(item) {
      if (this.isProcessing || !item) return;
      const index = this.rows.indexOf(item);
      if (index > -1) this.rows.splice(index, 1);
    },
    removeAllErrorRows() {
      if (this.isProcessing) return;
      const removed = this.errorRows.length;
      this.rows = this.validRows;
      this.showOnlyErrors = false;
      this.currentPage = 1;
      this.makeToastSuccess(`Đã xoá ${removed} dòng lỗi.`);
    },

    fetchApprovalProcesses() {
      this.approvalProcessesFailed = false;
      ApiService.get('v2/budget-line-request-imports/approval-processes')
        .then(({ data }) => {
          this.approvalProcesses = Array.isArray(data.data) ? data.data : [];
        })
        .catch((error) => {
          // Cờ riêng để tách "API hỏng" khỏi "chưa cấu hình quy trình nào".
          this.approvalProcesses = [];
          this.approvalProcessesFailed = true;
          handleError(error);
        });
    },
    onSelectApprovalProcess(configId) {
      const found = this.approvalProcesses.find(
        (p) => Number(p.approvalConfigId) === Number(configId),
      );
      if (!found) {
        this.approvalSteps = [];
        return;
      }
      this.approvalSteps = (found.details || []).map((detail) => ({
        step: detail.step,
        approverBy: null,
        owners: (detail.owners || []).map((owner) => ({
          id: Number(owner.id),
          name: owner.name,
          code: owner.code || '',
        })),
      }));
    },
    onApprovalStepsChange(steps) {
      this.approvalSteps = steps;
    },

    onConfirmImport() {
      if (!this.canCommit) return;
      this.$refs.confirmModal.show();
    },
    doCommitImport() {
      return this.withSubmitLock('commit', async () => {
        const payload = {
          approvalConfigId: this.selectedApprovalConfigId,
          approvers: this.approvalSteps
            .map((step) => ({ step: step.step, approverBy: step.approverBy }))
            .sort((a, b) => a.step - b.step),
          groups: this.groups.map((group) => ({
            companyId: group.companyId,
            departmentId: group.departmentId,
            storeId: group.storeId || null,
            costItemId: group.costItemId,
            reason: group.reason,
            periods: group.periods,
          })),
        };

        try {
          const { data } = await ApiService.post(
            'v2/budget-line-request-imports/commit',
            payload,
          );
          if (data.status !== 200) {
            this.makeToastFaile(data.message);
            return;
          }
          this.makeToastSuccess(
            data.message || `Đã tạo ${data.data.totalGroup} phiếu và gửi duyệt thành công`,
          );
          this.$router.push({ name: 'list-budget-line-requests' }).catch(() => {});
        } catch (error) {
          this.handleCommitError(error);
        }
      });
    },
    /**
     * Lỗi cấp-nhóm kèm `groupIndex` → chỉ ra phiếu nào hỏng.
     * Đọc `body.details`, KHÔNG phải `body.data` — middleware luôn để `data = {}`.
     */
    handleCommitError(error) {
      const body = error?.response?.data;
      const detail = body?.details;
      const groupIndex = detail?.groupIndex;
      if (Number.isInteger(groupIndex) && this.groups[groupIndex]) {
        const group = this.groups[groupIndex];
        this.makeToastFaile(
          `${body.message} — phiếu ${group.departmentName} / ${group.costItemName}`,
        );
        return;
      }
      handleError(error);
    },

    goBack() {
      this.$router.push({ name: 'list-budget-line-requests' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.preview-table >>> td,
.preview-table >>> th {
  vertical-align: middle;
  font-size: 0.9rem;
}
</style>
